import type { Editor } from '@tiptap/core';
import { Node } from '@tiptap/core';
import type { Node as ProseMirrorNode, ResolvedPos } from '@tiptap/pm/model';
import type { EditorState } from '@tiptap/pm/state';
import { TextSelection } from '@tiptap/pm/state';

import {
  BlockTypes,
  allBlocks,
  buildContentSchema,
  excludeBlocks,
} from '../blockTypes';

const columnContent = buildContentSchema(
  excludeBlocks(allBlocks, [
    BlockTypes.CAPTION,
    BlockTypes.HORIZONTAL_RULE,
    BlockTypes.COLUMNS,
    BlockTypes.TABLE,
    BlockTypes.METRIC_TABLE,
    BlockTypes.TABLE,
  ]),
);

const exitColumnsBlock = (
  editor: Editor,
  state: EditorState,
  columnsPos: number,
  columnsNodeSize: number,
) => {
  const columnsEndPos = columnsPos + columnsNodeSize;
  const { tr } = state;
  const $after = state.doc.resolve(columnsEndPos);
  if ($after.nodeAfter) {
    tr.setSelection(TextSelection.near($after));
  } else {
    const paragraphType = state.schema.nodes.paragraph;
    tr.insert(columnsEndPos, paragraphType.create());
    tr.setSelection(TextSelection.near(tr.doc.resolve(columnsEndPos + 1)));
  }
  editor.view.dispatch(tr);
};

const findAdjacentColumnPos = (
  columns: ProseMirrorNode,
  columnsPos: number,
  currentColumnPos: number,
  direction: 'next' | 'previous',
): number => {
  let result = -1;
  let foundCurrent = false;

  columns.forEach((child: ProseMirrorNode, offset: number) => {
    const childPos = columnsPos + 1 + offset;

    if (childPos === currentColumnPos) {
      foundCurrent = true;
      return;
    }

    if (child.type.name !== 'column') return;

    if (direction === 'next' && foundCurrent && result === -1) {
      result = childPos + 1;
    } else if (direction === 'previous' && !foundCurrent) {
      result = childPos + 1;
    }
  });

  return result;
};

// Helper to find column context at any nesting depth
const findColumnContext = ($from: ResolvedPos) => {
  for (let d = $from.depth; d > 0; d--) {
    const node = $from.node(d);
    if (node.type.name === 'column') {
      const parentNode = $from.node(d - 1);
      if (parentNode?.type.name === 'columns') {
        return {
          column: node,
          columns: parentNode,
          columnPos: $from.before(d),
          columnsPos: $from.before(d - 1),
          columnDepth: d,
        };
      }
    }
  }
  return null;
};

export const Column = Node.create({
  name: 'column',
  content: columnContent,
  isolating: true,
  addOptions() {
    return {
      HTMLAttributes: {
        class: 'editor-column',
        'data-column': '',
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-column]' }];
  },

  renderHTML({ HTMLAttributes }) {
    const parentClass = this.options.HTMLAttributes?.class ?? '';
    const extraClass = HTMLAttributes?.class ?? '';
    const mergedClass = [parentClass, extraClass].filter(Boolean).join(' ');

    return [
      'div',
      { ...this.options.HTMLAttributes, ...HTMLAttributes, class: mergedClass },
      0,
    ];
  },

  addKeyboardShortcuts() {
    return {
      // Navigate to next column with Tab
      Tab: ({ editor }) => {
        const { state } = editor;
        const { $from } = state.selection;

        const context = findColumnContext($from);
        if (!context) {
          return false;
        }

        const { columns, columnPos, columnsPos } = context;
        const nextPos = findAdjacentColumnPos(
          columns,
          columnsPos,
          columnPos,
          'next',
        );

        if (nextPos !== -1) {
          const { tr } = state;
          tr.setSelection(TextSelection.near(state.doc.resolve(nextPos)));
          editor.view.dispatch(tr);
          return true;
        }

        exitColumnsBlock(editor, state, columnsPos, columns.nodeSize);
        return true;
      },

      // Navigate to previous column with Shift+Tab
      'Shift-Tab': ({ editor }) => {
        const { state } = editor;
        const { $from } = state.selection;

        const context = findColumnContext($from);
        if (!context) {
          return false;
        }

        const { columns, columnPos, columnsPos } = context;
        const prevPos = findAdjacentColumnPos(
          columns,
          columnsPos,
          columnPos,
          'previous',
        );

        if (prevPos !== -1) {
          const { tr } = state;
          tr.setSelection(TextSelection.near(state.doc.resolve(prevPos)));
          editor.view.dispatch(tr);
          return true;
        }

        return false;
      },

      // Exit columns and create new line on arrow down
      ArrowDown: ({ editor }) => {
        const { state } = editor;
        const { selection } = state;
        const { $from } = selection;

        const context = findColumnContext($from);
        if (!context) {
          return false;
        }

        const { column, columnsPos, columns, columnPos } = context;

        // Check if we're at the end of the column
        // Calculate the absolute end position of the column (accounting for closing token)
        const columnEndPos = columnPos + column.nodeSize - 1;
        const atEnd = $from.pos >= columnEndPos;

        if (!atEnd) {
          return false;
        }

        exitColumnsBlock(editor, state, columnsPos, columns.nodeSize);
        return true;
      },

      // delete empty column on backspace
      Backspace: ({ editor }) => {
        const { state } = editor;
        const { selection } = state;
        const { $from } = selection;

        const context = findColumnContext($from);
        if (!context) {
          return false;
        }

        const { column, columns, columnPos, columnsPos } = context;

        const isAtStart = $from.parentOffset === 0;
        if (!isAtStart) {
          return false;
        }

        // Check if column is empty (only has one empty paragraph)
        const isColumnEmpty =
          column.childCount === 1 &&
          column.firstChild?.type.name === 'paragraph' &&
          column.firstChild?.textContent === '';

        if (!isColumnEmpty) {
          return false;
        }

        // If this is the last column, delete the entire columns block
        if (columns.childCount === 1) {
          editor
            .chain()
            .focus()
            .deleteRange({
              from: columnsPos,
              to: columnsPos + columns.nodeSize,
            })
            .run();
          return true;
        }

        // Delete just this column
        editor
          .chain()
          .focus()
          .deleteRange({
            from: columnPos,
            to: columnPos + column.nodeSize,
          })
          .run();
        return true;
      },
    };
  },
});
